import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Save, Send, Plus, Trash2, Upload, GripVertical, FlaskConical } from 'lucide-react';
import { PageHeader, SectionCard, StatusBadge } from '../components/ui';
import { useToast, ToastContainer } from '../components/ui';
import { API_BASE } from '../api';

interface VariantDraft {
  id?: string;
  title: string;
  sku: string;
  barcode: string;
  price: string;
  weight: string;
  weight_unit: string;
  inventory_quantity: string;
  options: Record<string, string>;
}

interface ImageDraft {
  id?: string;
  image_url: string;
  alt_text: string;
  sort_order: number;
  object_key?: string;
  file?: File;
  uploading?: boolean;
}

const CATEGORIES = [
  'general', 'apparel', 'accessories', 'beauty', 'home',
  'electronics', 'food', 'sports', 'toys', 'other',
];

const statusVariantMap: Record<string, 'success' | 'warning' | 'error' | 'info' | 'neutral'> = {
  draft: 'neutral',
  pending_review: 'warning',
  approved: 'info',
  active: 'success',
  paused: 'warning',
  archived: 'neutral',
};

function emptyVariant(): VariantDraft {
  return { title: '', sku: '', barcode: '', price: '', weight: '', weight_unit: 'kg', inventory_quantity: '0', options: {} };
}

export default function VendorProductForm() {
  const { t } = useTranslation(['products', 'common']);
  const { toasts, addToast, removeToast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [title, setTitle] = useState('');
  const [titleZh, setTitleZh] = useState('');
  const [description, setDescription] = useState('');
  const [descriptionZh, setDescriptionZh] = useState('');
  const [category, setCategory] = useState('general');
  const [tags, setTags] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [moq, setMoq] = useState('1');
  const [leadTimeDays, setLeadTimeDays] = useState('');
  const [isTest, setIsTest] = useState(false);
  const [status, setStatus] = useState('draft');
  const [shopifyProductId, setShopifyProductId] = useState<string | null>(null);

  const [variants, setVariants] = useState<VariantDraft[]>([emptyVariant()]);
  const [images, setImages] = useState<ImageDraft[]>([]);

  useEffect(() => {
    if (isEdit) loadProduct();
  }, [id]);

  const loadProduct = async () => {
    try {
      const res = await fetch(`${API_BASE}/vendor/products/${id}`, { credentials: 'include' });
      const json = await res.json();
      if (!res.ok) { addToast(json.error || t('common:error'), 'error'); return; }
      const p = json.data;
      setTitle(p.title || '');
      setTitleZh(p.title_zh || '');
      setDescription(p.description || '');
      setDescriptionZh(p.description_zh || '');
      setCategory(p.category || 'general');
      setTags((p.tags || []).join(', '));
      setPriceMin(p.price_min != null ? String(p.price_min) : '');
      setPriceMax(p.price_max != null ? String(p.price_max) : '');
      setMoq(String(p.moq ?? 1));
      setLeadTimeDays(p.lead_time_days != null ? String(p.lead_time_days) : '');
      setIsTest(p.is_test || false);
      setStatus(p.status || 'draft');
      setShopifyProductId(p.shopify_product_id || null);

      if (p.variants?.length) {
        setVariants(p.variants.map((v: any) => ({
          id: v.id,
          title: v.title || '',
          sku: v.sku || '',
          barcode: v.barcode || '',
          price: v.price != null ? String(v.price) : '',
          weight: v.weight != null ? String(v.weight) : '',
          weight_unit: v.weight_unit || 'kg',
          inventory_quantity: String(v.inventory_quantity ?? 0),
          options: v.options || {},
        })));
      }

      if (p.images?.length) {
        setImages(p.images.map((img: any) => ({
          id: img.id,
          image_url: img.image_url,
          alt_text: img.alt_text || '',
          sort_order: img.sort_order,
          object_key: img.object_key,
        })));
      }
    } catch {
      addToast(t('common:error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const buildPayload = () => {
    const tagArr = tags.split(',').map((t) => t.trim()).filter(Boolean);
    return {
      title,
      title_zh: titleZh || undefined,
      description: description || undefined,
      description_zh: descriptionZh || undefined,
      category,
      tags: tagArr.length > 0 ? tagArr : undefined,
      price_min: priceMin ? parseFloat(priceMin) : undefined,
      price_max: priceMax ? parseFloat(priceMax) : undefined,
      moq: parseInt(moq) || 1,
      lead_time_days: leadTimeDays ? parseInt(leadTimeDays) : undefined,
      is_test: isTest,
      variants: variants.filter((v) => v.title || v.sku || v.price).map((v) => ({
        title: v.title || undefined,
        sku: v.sku || undefined,
        barcode: v.barcode || undefined,
        price: v.price ? parseFloat(v.price) : undefined,
        weight: v.weight ? parseFloat(v.weight) : undefined,
        weight_unit: v.weight_unit || undefined,
        inventory_quantity: parseInt(v.inventory_quantity) || 0,
        options: Object.keys(v.options).length > 0 ? v.options : undefined,
      })),
      images: images.filter((img) => img.image_url && !img.id).map((img) => ({
        image_url: img.image_url,
        alt_text: img.alt_text || undefined,
        sort_order: img.sort_order,
        object_key: img.object_key,
      })),
    };
  };

  const handleSave = async () => {
    if (!title.trim()) { addToast(t('common:required'), 'error'); return; }
    setSaving(true);
    try {
      const url = isEdit ? `${API_BASE}/vendor/products/${id}` : `${API_BASE}/vendor/products`;
      const method = isEdit ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload()),
      });
      const json = await res.json();
      if (res.ok) {
        addToast(t('common:success'), 'success');
        if (!isEdit) navigate(`/vendor/products/${json.data.id}/edit`, { replace: true });
      } else {
        addToast(json.error || t('common:error'), 'error');
      }
    } catch {
      addToast(t('common:error'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForReview = async () => {
    if (!isEdit) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/vendor/products/${id}/submit`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        setStatus('pending_review');
        addToast(t('common:success'), 'success');
      } else {
        const json = await res.json();
        addToast(json.error || t('common:error'), 'error');
      }
    } catch {
      addToast(t('common:error'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const uploadImage = async (file: File) => {
    const idx = images.length;
    const tempDraft: ImageDraft = { image_url: URL.createObjectURL(file), alt_text: '', sort_order: idx, file, uploading: true };
    setImages((prev) => [...prev, tempDraft]);

    try {
      const presignRes = await fetch(`${API_BASE}/admin/uploads/vendor-product-image/presign`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: id || 'new', fileName: file.name, contentType: file.type, sizeBytes: file.size }),
      });
      const presign = await presignRes.json();
      if (!presignRes.ok) throw new Error(presign.error);

      const uploadRes = await fetch(presign.uploadUrl, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!uploadRes.ok) throw new Error('Upload failed');

      setImages((prev) => prev.map((img, i) =>
        i === idx ? { ...img, image_url: presign.publicUrl, object_key: presign.objectKey, uploading: false, file: undefined } : img
      ));

      if (isEdit) {
        await fetch(`${API_BASE}/vendor/products/${id}/images`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image_url: presign.publicUrl, object_key: presign.objectKey, sort_order: idx }),
        });
      }
    } catch (err) {
      addToast(String(err), 'error');
      setImages((prev) => prev.filter((_, i) => i !== idx));
    }
  };

  const handleFileDrop = (files: FileList | null) => {
    if (!files) return;
    for (const file of Array.from(files)) {
      if (['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
        uploadImage(file);
      }
    }
  };

  const removeImage = async (idx: number) => {
    const img = images[idx];
    if (img.id && isEdit) {
      await fetch(`${API_BASE}/vendor/products/${id}/images/${img.id}`, { method: 'DELETE', credentials: 'include' });
    }
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateVariant = (idx: number, field: keyof VariantDraft, value: string) => {
    setVariants((prev) => prev.map((v, i) => i === idx ? { ...v, [field]: value } : v));
  };

  if (loading) {
    return <div className="p-8"><div className="h-8 w-48 bg-white/5 animate-pulse rounded" /></div>;
  }

  const canSubmit = isEdit && status === 'draft';
  const isReadOnly = ['pending_review', 'approved', 'active'].includes(status);

  return (
    <div className="space-y-6 max-w-5xl">
      <PageHeader
        title={isEdit ? t('products:editProduct') : t('products:newProduct')}
        subtitle={isEdit ? `ID: ${id}` : undefined}
        back={{ label: t('products:backToProducts'), onClick: () => navigate('/vendor/products') }}
        actions={
          <div className="flex items-center gap-2">
            {isEdit && (
              <StatusBadge variant={statusVariantMap[status] || 'neutral'}>
                {t(`products:status.${status}`)}
              </StatusBadge>
            )}
            {shopifyProductId && (
              <span className="text-xs text-white/30">Shopify #{shopifyProductId}</span>
            )}
          </div>
        }
      />

      {/* Basic Information */}
      <SectionCard title={t('products:basicInfo')}>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">{t('products:fields.title')} *</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} className="input" disabled={isReadOnly} />
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">{t('products:fields.titleZh')}</label>
              <input value={titleZh} onChange={(e) => setTitleZh(e.target.value)} className="input" disabled={isReadOnly} />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">{t('products:fields.description')}</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="input min-h-[80px]" disabled={isReadOnly} />
          </div>
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">{t('products:fields.descriptionZh')}</label>
            <textarea value={descriptionZh} onChange={(e) => setDescriptionZh(e.target.value)} className="input min-h-[80px]" disabled={isReadOnly} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">{t('products:category')}</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="input" disabled={isReadOnly}>
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{t(`products:categories.${cat}`)}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">{t('products:tags')}</label>
              <input value={tags} onChange={(e) => setTags(e.target.value)} className="input" placeholder={t('products:tagsHint')} disabled={isReadOnly} />
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Media */}
      <SectionCard title={t('products:media')}>
        <div className="p-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {images.map((img, idx) => (
              <div key={idx} className="relative group aspect-square rounded overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
                {img.uploading ? (
                  <div className="flex items-center justify-center h-full bg-white/5">
                    <div className="w-6 h-6 border-2 border-[#FFD400] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  <img src={img.image_url} alt={img.alt_text} className="w-full h-full object-cover" referrerPolicy="no-referrer" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                )}
                {!isReadOnly && (
                  <button
                    onClick={() => removeImage(idx)}
                    className="absolute top-1 right-1 p-1 bg-black/70 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-3 h-3 text-red-400" />
                  </button>
                )}
                <div className="absolute top-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <GripVertical className="w-3 h-3 text-white/50" />
                </div>
              </div>
            ))}

            {!isReadOnly && images.length < 10 && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="aspect-square rounded flex flex-col items-center justify-center gap-1.5 text-white/30 hover:text-white/60 transition-colors"
                style={{ border: '1px dashed rgba(255,255,255,0.1)' }}
                onDrop={(e) => { e.preventDefault(); handleFileDrop(e.dataTransfer.files); }}
                onDragOver={(e) => e.preventDefault()}
              >
                <Upload className="w-5 h-5" />
                <span className="text-xs">{t('products:uploadImage')}</span>
              </button>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={(e) => handleFileDrop(e.target.files)} />
        </div>
      </SectionCard>

      {/* Pricing */}
      <SectionCard title={t('products:pricing')}>
        <div className="p-5 grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">{t('products:fields.baseCost')}</label>
            <input type="number" step="0.01" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} className="input" disabled={isReadOnly} />
          </div>
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">{t('products:fields.suggestedRetail')}</label>
            <input type="number" step="0.01" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} className="input" disabled={isReadOnly} />
          </div>
        </div>
      </SectionCard>

      {/* Inventory & Lead Time */}
      <SectionCard title={t('products:inventory')}>
        <div className="p-5 grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">{t('products:moq')}</label>
            <input type="number" value={moq} onChange={(e) => setMoq(e.target.value)} className="input" disabled={isReadOnly} />
          </div>
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">{t('products:leadTime')}</label>
            <input type="number" value={leadTimeDays} onChange={(e) => setLeadTimeDays(e.target.value)} className="input" disabled={isReadOnly} />
          </div>
        </div>
      </SectionCard>

      {/* Variants */}
      <SectionCard
        title={t('products:variants')}
        actions={
          !isReadOnly && (
            <button onClick={() => setVariants((prev) => [...prev, emptyVariant()])} className="btn-ghost flex items-center gap-1.5 text-sm">
              <Plus className="w-3.5 h-3.5" />
              {t('products:addVariant')}
            </button>
          )
        }
      >
        <div className="p-5 space-y-3">
          {variants.map((v, idx) => (
            <div key={idx} className="grid grid-cols-6 gap-2 items-end p-3 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
              <div className="space-y-1">
                <label className="block text-[10px] text-white/40 uppercase">{t('products:variantTitle')}</label>
                <input value={v.title} onChange={(e) => updateVariant(idx, 'title', e.target.value)} className="input text-sm" disabled={isReadOnly} />
              </div>
              <div className="space-y-1">
                <label className="block text-[10px] text-white/40 uppercase">{t('products:fields.sku')}</label>
                <input value={v.sku} onChange={(e) => updateVariant(idx, 'sku', e.target.value)} className="input text-sm" disabled={isReadOnly} />
              </div>
              <div className="space-y-1">
                <label className="block text-[10px] text-white/40 uppercase">{t('products:price')}</label>
                <input type="number" step="0.01" value={v.price} onChange={(e) => updateVariant(idx, 'price', e.target.value)} className="input text-sm" disabled={isReadOnly} />
              </div>
              <div className="space-y-1">
                <label className="block text-[10px] text-white/40 uppercase">{t('products:fields.weight')}</label>
                <input type="number" step="0.01" value={v.weight} onChange={(e) => updateVariant(idx, 'weight', e.target.value)} className="input text-sm" disabled={isReadOnly} />
              </div>
              <div className="space-y-1">
                <label className="block text-[10px] text-white/40 uppercase">{t('products:fields.inventoryQty')}</label>
                <input type="number" value={v.inventory_quantity} onChange={(e) => updateVariant(idx, 'inventory_quantity', e.target.value)} className="input text-sm" disabled={isReadOnly} />
              </div>
              <div className="flex items-end">
                {!isReadOnly && variants.length > 1 && (
                  <button onClick={() => setVariants((prev) => prev.filter((_, i) => i !== idx))} className="p-2 text-white/20 hover:text-red-400 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Options */}
      <SectionCard title={t('products:options')}>
        <div className="p-5 flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
            <input type="checkbox" checked={isTest} onChange={(e) => setIsTest(e.target.checked)} className="accent-[#FFD400]" disabled={isReadOnly} />
            <FlaskConical className="w-4 h-4 text-white/40" />
            {t('products:isTest')}
          </label>
        </div>
        {isTest && (
          <p className="px-5 pb-4 text-xs text-white/30">{t('products:isTestHint')}</p>
        )}
      </SectionCard>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-4 pb-8">
        {!isReadOnly && (
          <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2">
            <Save className="w-4 h-4" />
            {saving ? '...' : (isEdit ? t('products:saveChanges') : t('products:saveDraft'))}
          </button>
        )}
        {canSubmit && (
          <button onClick={handleSubmitForReview} disabled={submitting} className="btn-secondary flex items-center gap-2">
            <Send className="w-4 h-4" />
            {submitting ? '...' : t('products:submitForReview')}
          </button>
        )}
        <button onClick={() => navigate('/vendor/products')} className="btn-ghost">{t('common:cancel')}</button>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}
